import React from "react";
import WebProfitSpinner from "../../system/WebProfitSpinner";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditableImage from "./EditableImage";
import GoogleFontPicker from "./GoogleFontPicker";
import { buildGoogleFontsUrl } from "./googleFonts";
import {
  LANDING_LAYOUT_MAX_SEGMENTS,
  addLandingBaseSegment,
  appendLandingSegment,
  isCanonicalLandingSegment,
  createCustomLandingSegment,
  getLandingSegments,
  landingSegmentAnchor,
  moveLandingSegment,
  removeLandingSegment,
  updateLandingSegment,
} from "./landingLayout";
import LandingCopyTextField from "../landingStudio/inspector/LandingCopyTextField";
import {
  SEGMENT_ORDER,
  buildSegmentVisibilityChanges,
  getSegmentDefinition,
} from "../landingStudio/segmentRegistry";

import {
  TAB_DEFINITIONS,
  tabsForMode,
  dialogTitle,
  dialogDescription,
  clone,
  normalizePublicStatus,
  publicStatusLabel,
  withoutPublicStatus,
  statusConfirmationMessage,
  getAtPath,
  setAtPath,
  Field,
  ColorField,
  SECTION_GROUPS,
  SegmentChoiceCard
} from "./settings/LandingSettingsDialogPrimitives";

export default function LandingSettingsDialog({
  open,
  form,
  baselineForm,
  productsEnabled = true,
  saving = false,
  changingPublicStatus = false,
  initialTab = 0,
  requestedPublicStatus = "",
  mode = "legacy",
  showTrialGuide = false,
  onClose,
  onSave,
  onChangePublicStatus,
  onUploadAsset,
  onDeleteAsset,
}) {
  const compactViewport = useMediaQuery("(max-width:700px)");
  const touchViewport = useMediaQuery("(max-width:1100px)");
  const allowedTabs = React.useMemo(() => tabsForMode(mode), [mode]);
  const initialTabValue = allowedTabs.includes(Number(initialTab)) ? Number(initialTab) : allowedTabs[0];
  const [draft, setDraft] = React.useState(() => clone(form || {}));
  const [tab, setTab] = React.useState(initialTabValue);
  const [error, setError] = React.useState("");
  const [pendingPublicStatus, setPendingPublicStatus] = React.useState("");
  const [pendingCustomRemovalId, setPendingCustomRemovalId] = React.useState("");

  React.useEffect(() => {
    if (open) {
      const nextAllowedTabs = tabsForMode(mode);
      const requestedTab = Number(initialTab);
      const currentStatus = normalizePublicStatus(form?.publicStatus);
      const requestedStatus = requestedPublicStatus
        ? normalizePublicStatus(requestedPublicStatus)
        : "";
      setDraft({
        ...clone(form || {}),
        ...(requestedStatus ? { publicStatus: requestedStatus } : {}),
      });
      setTab(nextAllowedTabs.includes(requestedTab) ? requestedTab : nextAllowedTabs[0]);
      setError("");
      setPendingPublicStatus(requestedStatus && requestedStatus !== currentStatus ? requestedStatus : "");
      setPendingCustomRemovalId("");
    }
  }, [form, initialTab, mode, open, requestedPublicStatus]);

  const change = (path, value) => setDraft((current) => setAtPath(current, path, value));
  const changeMany = React.useCallback((changes) => {
    setDraft((current) =>
      Object.entries(changes || {}).reduce(
        (next, [path, value]) => setAtPath(next, path, value),
        current
      )
    );
  }, []);
  const changeGlobalColor = React.useCallback((path, value) => {
    changeMany({ [path]: value });
  }, [changeMany]);

  const changeGoogleFonts = (headingFamily, bodyFamily) => {
    const heading = headingFamily || draft?.theme?.headingFontFamily || "Exo 2";
    const body = bodyFamily || draft?.theme?.bodyFontFamily || "Asap";
    changeMany({
      "theme.fontFamily": body,
      "theme.headingFontFamily": heading,
      "theme.bodyFontFamily": body,
      "theme.googleFontsUrl": buildGoogleFontsUrl(heading, body),
    });
  };

  const segmentDefinitions = React.useMemo(
    () => SEGMENT_ORDER.map((segmentType) => getSegmentDefinition(segmentType)).filter(Boolean),
    []
  );
  const layoutSegments = React.useMemo(() => getLandingSegments(draft), [draft?.layout]);
  const editableLayoutSegments = React.useMemo(
    () => layoutSegments.filter((segment) => !["navbar", "footer"].includes(segment.type)),
    [layoutSegments]
  );

  const updateLayout = React.useCallback((nextLayout) => {
    setDraft((current) => ({ ...current, layout: nextLayout }));
  }, []);

  const setBaseSectionPresence = React.useCallback((definition, nextVisible) => {
    setDraft((current) => {
      let next = clone(current);
      next.layout = nextVisible
        ? addLandingBaseSegment(current.layout, definition.id)
        : removeLandingSegment(current.layout, definition.id);
      Object.entries(buildSegmentVisibilityChanges(definition.id, nextVisible)).forEach(([path, value]) => {
        next = setAtPath(next, path, value);
      });
      return next;
    });
  }, []);

  const addCustomSection = React.useCallback(() => {
    setDraft((current) => {
      const segment = createCustomLandingSegment(current.layout);
      if (!segment) return current;
      return { ...current, layout: appendLandingSegment(current.layout, segment) };
    });
  }, []);

  const removeLayoutSegment = React.useCallback((segment) => {
    setDraft((current) => {
      let next = { ...current, layout: removeLandingSegment(current.layout, segment.id) };
      if (isCanonicalLandingSegment(segment)) {
        Object.entries(buildSegmentVisibilityChanges(segment.type, false)).forEach(([path, value]) => {
          next = setAtPath(next, path, value);
        });
      }
      return next;
    });
  }, []);

  const requestRemoveLayoutSegment = React.useCallback((segment) => {
    if (segment?.type === "custom") {
      setPendingCustomRemovalId(segment.id);
      return;
    }
    removeLayoutSegment(segment);
  }, [removeLayoutSegment]);

  const moveLayoutSegment = React.useCallback((segmentId, direction) => {
    setDraft((current) => ({
      ...current,
      layout: moveLandingSegment(current.layout, segmentId, direction),
    }));
  }, []);

  const changeSegmentAnchor = React.useCallback((segmentId, value) => {
    setDraft((current) => ({
      ...current,
      layout: updateLandingSegment(current.layout, segmentId, { anchor: value }),
    }));
  }, []);

  const setLayoutSegmentVisibility = React.useCallback((segment, nextVisible) => {
    setDraft((current) => {
      if (!segment) return current;
      if (isCanonicalLandingSegment(segment)) {
        let next = current;
        Object.entries(buildSegmentVisibilityChanges(segment.type, nextVisible)).forEach(([path, value]) => {
          next = setAtPath(next, path, value);
        });
        return next;
      }
      return {
        ...current,
        layout: updateLandingSegment(current.layout, segment.id, { visible: nextVisible === true }),
      };
    });
  }, []);

  const upload = async (path, file, metadata, options = {}) => {
    const asset = await onUploadAsset?.(file, metadata, options);
    const value = asset?.value || asset?.publicPath || asset?.url || "";
    if (!value) throw new Error("No se recibió la ruta del archivo subido");
    change(path, value);
  };

  const remove = async (path) => {
    const current = getAtPath(draft, path, "");
    if (current) await onDeleteAsset?.(current);
    change(path, "");
  };

  const pendingCustomRemoval = React.useMemo(
    () => layoutSegments.find(
      (segment) => segment.id === pendingCustomRemovalId && segment.type === "custom"
    ) || null,
    [layoutSegments, pendingCustomRemovalId]
  );
  const pendingCustomRemovalLabel = React.useMemo(() => {
    const title = getAtPath(pendingCustomRemoval, "data.custom.title", "");
    return String(title || "").trim() || "Sección libre";
  }, [pendingCustomRemoval]);

  const confirmCustomRemoval = React.useCallback(() => {
    if (!pendingCustomRemoval) {
      setPendingCustomRemovalId("");
      return;
    }
    removeLayoutSegment(pendingCustomRemoval);
    setPendingCustomRemovalId("");
  }, [pendingCustomRemoval, removeLayoutSegment]);

  const currentPublicStatus = normalizePublicStatus(form?.publicStatus);
  const selectedPublicStatus = normalizePublicStatus(draft?.publicStatus);
  const publicStatusChanged = selectedPublicStatus !== currentPublicStatus;
  const contentChanged = React.useMemo(
    () =>
      JSON.stringify(withoutPublicStatus(draft)) !==
      JSON.stringify(withoutPublicStatus(form)),
    [draft, form]
  );
  const busy = saving || changingPublicStatus;

  const persist = async (nextPublicStatus = "") => {
    try {
      setError("");

      if (contentChanged) {
        await onSave?.(
          { ...draft, publicStatus: currentPublicStatus },
          {
            editorKey: `settings:${tab}`,
            editorType: "settings",
            tabIndex: tab,
            sectionLabel: TAB_DEFINITIONS[tab]?.sectionLabel || "Preferencias del sitio",
          }
        );
      }

      if (nextPublicStatus) {
        const result = await onChangePublicStatus?.(nextPublicStatus);
        if (!result) {
          throw new Error("No se pudo confirmar el cambio de estado público.");
        }
      }

      setPendingPublicStatus("");
      onClose?.();
    } catch (err) {
      setError(err?.message || "No se pudieron guardar los ajustes");
    }
  };

  const save = async () => {
    if (publicStatusChanged) {
      setPendingPublicStatus(selectedPublicStatus);
      return;
    }
    await persist();
  };

  return (
    <>
      <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      fullScreen={compactViewport}
      maxWidth="md"
      sx={touchViewport ? {
        "& .MuiButton-root": { minHeight: 44 },
        "& .MuiIconButton-root": { minWidth: 44, minHeight: 44 },
        "& .MuiInputBase-root": { minHeight: 44 },
        "& .MuiTab-root": { minHeight: 48 },
        "& input[type='color']": { minWidth: 48, minHeight: 44 },
      } : undefined}
    >
      <DialogTitle sx={{ pb: 0.5, fontWeight: 950, color: "#19242F" }}>{dialogTitle(mode)}</DialogTitle>
      <Typography variant="body2" sx={{ px: { xs: 2, sm: 3 }, pb: allowedTabs.length > 1 ? 0.5 : 1.5, color: "rgba(25,36,47,.62)" }}>
        {dialogDescription(mode)}
      </Typography>
      {allowedTabs.length > 1 ? (
        <Box sx={{ px: { xs: 0.75, sm: 2.5 }, pt: 0.5 }}>
          <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" scrollButtons="auto">
            {allowedTabs.map((value) => (
              <Tab key={value} value={value} label={TAB_DEFINITIONS[value]?.label || "Ajustes"} sx={{ textTransform: "none", fontWeight: 850 }} />
            ))}
          </Tabs>
        </Box>
      ) : null}
      <DialogContent dividers>
        <Stack spacing={2.2}>
          {showTrialGuide && mode === "global" ? (
            <Alert severity="info">Las preferencias generales pueden afinarse después. Para textos, imágenes y visibilidad de bloques, editá directamente cada sección.</Alert>
          ) : showTrialGuide && mode === "legacy" ? (
            <Alert severity="info">Completá primero identidad, portada, contacto y publicación. Las imágenes avanzadas pueden agregarse después.</Alert>
          ) : null}

          {tab === 0 ? (
            <Stack spacing={2}>
              <Typography sx={{ fontWeight: 950 }}>Paleta general</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><ColorField form={draft} path="theme.primaryColor" label="Color principal" onChange={changeGlobalColor} /></Grid>
                <Grid item xs={12} sm={6}><ColorField form={draft} path="theme.secondaryColor" label="Color oscuro" onChange={changeGlobalColor} /></Grid>
                <Grid item xs={12} sm={6}><ColorField form={draft} path="theme.surfaceColor" label="Superficie" onChange={changeGlobalColor} /></Grid>
                <Grid item xs={12} sm={6}><ColorField form={draft} path="theme.backgroundColor" label="Fondo general" onChange={changeGlobalColor} /></Grid>
                <Grid item xs={12} sm={6}><ColorField form={draft} path="theme.textColor" label="Texto principal" onChange={changeGlobalColor} /></Grid>
                <Grid item xs={12} sm={6}><ColorField form={draft} path="theme.mutedTextColor" label="Texto secundario" onChange={changeGlobalColor} /></Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <GoogleFontPicker
                    label="Tipografía de títulos"
                    value={draft?.theme?.headingFontFamily}
                    disabled={busy}
                    onChange={(family) => changeGoogleFonts(family, draft?.theme?.bodyFontFamily)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <GoogleFontPicker
                    label="Tipografía de textos"
                    value={draft?.theme?.bodyFontFamily}
                    disabled={busy}
                    onChange={(family) => changeGoogleFonts(draft?.theme?.headingFontFamily, family)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: "#F7F8FA", border: "1px solid rgba(25,36,47,.08)" }}>
                    <Typography
                      sx={{
                        fontFamily: `"${draft?.theme?.headingFontFamily || "Exo 2"}", sans-serif`,
                        fontWeight: 800,
                        fontSize: 22,
                        color: "#19242F",
                      }}
                    >
                      Vista previa de títulos
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.5,
                        fontFamily: `"${draft?.theme?.bodyFontFamily || "Asap"}", sans-serif`,
                        color: "rgba(25,36,47,.66)",
                      }}
                    >
                      WebProfit carga Google Fonts automáticamente en la web publicada.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}><Field form={draft} path="theme.borderRadius" label="Redondeo general" type="number" onChange={(path, value) => change(path, Number(value) || 0)} /></Grid>
              </Grid>
            </Stack>
          ) : null}

          {tab === 1 ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <EditableImage
                  value={getAtPath(draft, "theme.logoUrl", "")}
                  label="Logo principal"
                  helperText="SVG, PNG, WebP o JPG."
                  disabled={saving}
                  onUpload={(file, options) => upload("theme.logoUrl", file, { sectionKey: "branding", assetKey: "logo" }, options)}
                  onRemove={() => remove("theme.logoUrl")}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <EditableImage
                  value={getAtPath(draft, "theme.compactLogoUrl", "")}
                  label="Logo compacto"
                  helperText="Versión cuadrada o reducida para celular."
                  disabled={saving}
                  onUpload={(file, options) => upload("theme.compactLogoUrl", file, { sectionKey: "branding", assetKey: "compact-logo" }, options)}
                  onRemove={() => remove("theme.compactLogoUrl")}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <EditableImage
                  value={getAtPath(draft, "theme.faviconUrl", "")}
                  label="Favicon"
                  helperText="Ícono cuadrado para la pestaña."
                  disabled={saving}
                  accept="image/svg+xml,image/png,image/webp,image/x-icon,.svg,.ico"
                  onUpload={(file, options) => upload("theme.faviconUrl", file, { sectionKey: "branding", assetKey: "favicon" }, options)}
                  onRemove={() => remove("theme.faviconUrl")}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <EditableImage
                  value={getAtPath(draft, "hero.imageUrl", "")}
                  label="Imagen principal de portada"
                  helperText="Se usa cuando la portada no tiene slider."
                  disabled={saving}
                  onUpload={(file, options) => upload("hero.imageUrl", file, { sectionKey: "hero", assetKey: "background" }, options)}
                  onRemove={() => remove("hero.imageUrl")}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <EditableImage
                  value={getAtPath(draft, "about.imageUrl", "")}
                  label="Imagen sobre el negocio"
                  disabled={saving}
                  onUpload={(file, options) => upload("about.imageUrl", file, { sectionKey: "about", assetKey: "main" }, options)}
                  onRemove={() => remove("about.imageUrl")}
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }} />
                <Typography sx={{ mt: 1, fontWeight: 950, color: "#19242F" }}>Diferenciales</Typography>
                <Typography variant="body2" sx={{ mt: 0.35, color: "rgba(25,36,47,.6)" }}>
                  La foto de fondo es opcional. Cada diferencial también puede llevar su propia imagen de tarjeta; si no tiene imagen, se muestra el ícono elegido.
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <EditableImage
                  value={getAtPath(draft, "highlightsIntro.backgroundImageUrl", "")}
                  label="Foto de fondo de diferenciales"
                  helperText="Opcional. Se aplica detrás de toda la sección manteniendo legibles las tarjetas."
                  disabled={saving}
                  onUpload={(file, options) => upload("highlightsIntro.backgroundImageUrl", file, { sectionKey: "highlights", assetKey: "background" }, options)}
                  onRemove={() => remove("highlightsIntro.backgroundImageUrl")}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Posición del fondo"
                  value={getAtPath(draft, "highlightsIntro.backgroundImagePosition", "center")}
                  onChange={(event) => change("highlightsIntro.backgroundImagePosition", event.target.value)}
                >
                  <MenuItem value="center">Centro</MenuItem>
                  <MenuItem value="top">Arriba</MenuItem>
                  <MenuItem value="bottom">Abajo</MenuItem>
                  <MenuItem value="left">Izquierda</MenuItem>
                  <MenuItem value="right">Derecha</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Intensidad del fondo"
                  value={getAtPath(draft, "highlightsIntro.backgroundImageOpacity", 0.24)}
                  inputProps={{ min: 0, max: 1, step: 0.05 }}
                  helperText="0 = invisible · 1 = completa"
                  onChange={(event) => {
                    const value = Number(event.target.value);
                    change("highlightsIntro.backgroundImageOpacity", Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0.24);
                  }}
                />
              </Grid>
              {(Array.isArray(draft?.highlights) ? draft.highlights : []).slice(0, 24).map((item, index) => (
                <Grid item xs={12} md={6} key={item?.id || `highlight-image-${index}`}>
                  <EditableImage
                    value={getAtPath(draft, `highlights.${index}.imageUrl`, "")}
                    label={`Imagen de tarjeta · ${item?.title || `Diferencial ${index + 1}`}`}
                    helperText="Opcional. Puede convivir con el video de YouTube; si no hay imagen ni ícono, no se reserva ningún hueco."
                    disabled={saving}
                    onUpload={(file, options) => upload(`highlights.${index}.imageUrl`, file, { sectionKey: "highlights", assetKey: `card-${index + 1}` }, options)}
                    onRemove={() => remove(`highlights.${index}.imageUrl`)}
                  />
                </Grid>
              ))}
              <Grid item xs={12} md={6}>
                <EditableImage
                  value={getAtPath(draft, "seo.imageUrl", "")}
                  label="Imagen para compartir"
                  helperText="Vista previa para redes sociales y mensajería."
                  disabled={saving}
                  onUpload={(file, options) => upload("seo.imageUrl", file, { sectionKey: "seo", assetKey: "social" }, options)}
                  onRemove={() => remove("seo.imageUrl")}
                />
              </Grid>
            </Grid>
          ) : null}

          {tab === 2 ? (
            <Stack spacing={2.4}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 950, color: "#19242F" }}>
                  Secciones reales de la landing
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.45, color: "rgba(25,36,47,.62)" }}>
                  Agregá, quitá y ordená bloques. Las secciones nuevas se crean acá de forma simple y su contenido se edita directamente desde la landing.
                </Typography>
              </Box>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between">
                <Button
                  variant="contained"
                  startIcon={<AddRoundedIcon />}
                  disabled={layoutSegments.length >= LANDING_LAYOUT_MAX_SEGMENTS}
                  onClick={addCustomSection}
                  sx={{ textTransform: "none", fontWeight: 900, borderRadius: 2.2 }}
                >
                  Agregar nueva sección
                </Button>
                <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)" }}>
                  {editableLayoutSegments.length} bloques en la landing · máximo técnico {LANDING_LAYOUT_MAX_SEGMENTS - 2} de contenido
                </Typography>
              </Stack>

              <Alert severity="info">
                Cada sección nueva tiene un título y un código de ancla. Después de guardarla, seleccionala directamente en la landing para cargar contenido, imagen, video, botón y diseño.
              </Alert>

              <Stack spacing={1.35}>
                {editableLayoutSegments.map((segment, visibleIndex) => {
                  const layoutIndex = layoutSegments.findIndex((candidate) => candidate.id === segment.id);
                  const definition = getSegmentDefinition(segment.type);
                  const custom = segment.type === "custom";
                  const anchor = landingSegmentAnchor(segment);
                  const customPath = `layout.segments.${layoutIndex}.data.custom`;
                  const visible = isCanonicalLandingSegment(segment)
                    ? getAtPath(draft, definition?.visibilityPath, definition?.defaultVisible) !== false
                    : segment.visible !== false;
                  return (
                    <Box
                      key={segment.id}
                      sx={{
                        p: 1.5,
                        borderRadius: 3,
                        border: "1px solid rgba(25,36,47,.10)",
                        bgcolor: custom ? "rgba(243,90,58,.025)" : "#fff",
                      }}
                    >
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between">
                        <Box sx={{ minWidth: 0 }}>
                          <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" alignItems="center">
                            <Typography sx={{ fontWeight: 950, color: "#19242F" }}>
                              {custom ? getAtPath(draft, `${customPath}.title`, "Nueva sección") || "Sección libre" : definition?.label || segment.type}
                            </Typography>
                            <Chip size="small" label={custom ? "Libre" : "Bloque"} variant="outlined" />
                            <Chip size="small" label={anchor || "Sin ancla"} sx={{ fontFamily: "monospace" }} />
                          </Stack>
                          <Typography variant="caption" sx={{ display: "block", mt: 0.35, color: "rgba(25,36,47,.56)" }}>
                            Posición {visibleIndex + 1} · {custom ? "contenido creado por el negocio" : definition?.presentation?.summary || "sección del sitio"}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap">
                          <Button
                            size="small"
                            variant={visible ? "outlined" : "contained"}
                            onClick={() => setLayoutSegmentVisibility(segment, !visible)}
                            sx={{ textTransform: "none", fontWeight: 850 }}
                          >
                            {visible ? "Ocultar" : "Mostrar"}
                          </Button>
                          <Button size="small" variant="outlined" disabled={visibleIndex === 0} onClick={() => moveLayoutSegment(segment.id, -1)} sx={{ textTransform: "none", fontWeight: 850 }}>Subir</Button>
                          <Button size="small" variant="outlined" disabled={visibleIndex === editableLayoutSegments.length - 1} onClick={() => moveLayoutSegment(segment.id, 1)} sx={{ textTransform: "none", fontWeight: 850 }}>Bajar</Button>
                          <Button size="small" color="error" variant="text" onClick={() => requestRemoveLayoutSegment(segment)} sx={{ textTransform: "none", fontWeight: 850 }}>Quitar</Button>
                        </Stack>
                      </Stack>

                      {custom ? (
                        <>
                          <Divider sx={{ my: 1.5 }} />
                          <Grid container spacing={1.4}>
                            <Grid item xs={12} md={7}>
                              <Field
                                form={draft}
                                path={`${customPath}.title`}
                                label="Título de la sección"
                                onChange={change}
                              />
                            </Grid>
                            <Grid item xs={12} md={5}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Código / ancla"
                                defaultValue={String(segment.anchor || segment.id)}
                                helperText="Sin #. Ej.: ceremonias, equipo, testimonios."
                                onBlur={(event) => changeSegmentAnchor(segment.id, event.target.value)}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)" }}>
                                El resto se edita haciendo clic en esta sección dentro de la landing: contenido, posición izquierda/derecha, imagen, YouTube, Calendly y botón.
                              </Typography>
                            </Grid>
                          </Grid>
                        </>
                      ) : null}
                    </Box>
                  );
                })}
              </Stack>

              <Divider />
              <Box>
                <Typography sx={{ fontWeight: 950, color: "#19242F" }}>Bloques prediseñados</Typography>
                <Typography variant="body2" sx={{ mt: 0.35, color: "rgba(25,36,47,.60)" }}>
                  Estos siguen disponibles, pero ahora agregarlos o quitarlos modifica de verdad la estructura de la landing.
                </Typography>
              </Box>

              {!productsEnabled ? (
                <Alert severity="info">Los bloques comerciales requieren el módulo de productos o servicios.</Alert>
              ) : null}

              {SECTION_GROUPS.map((group) => {
                const definitions = segmentDefinitions.filter(
                  (definition) => definition.presentation?.group === group.id && !["navbar", "footer", "floatingWhatsapp"].includes(definition.id)
                );
                if (!definitions.length) return null;
                const commercialGroup = group.id === "commerce";
                const commercialEnabled = getAtPath(draft, "sections.showProducts", true) !== false;

                return (
                  <Box key={group.id}>
                    <Typography sx={{ mb: 1, fontWeight: 950, color: "#19242F" }}>{group.title}</Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0,1fr)", md: "repeat(2, minmax(0,1fr))" }, gap: 1.35 }}>
                      {definitions.map((definition) => {
                        const inLayout = layoutSegments.some((segment) => segment.id === definition.id);
                        const ownVisible = definition.visibilityPath
                          ? getAtPath(draft, definition.visibilityPath, definition.defaultVisible) !== false
                          : true;
                        const active = inLayout && (definition.requiresProducts ? productsEnabled && commercialEnabled && ownVisible : ownVisible);
                        const disabled = definition.requiresProducts && !productsEnabled;
                        return (
                          <SegmentChoiceCard
                            key={definition.id}
                            definition={definition}
                            active={active}
                            disabled={disabled}
                            onToggle={(nextVisible) => setBaseSectionPresence(definition, nextVisible)}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          ) : null}

          {tab === 3 ? (
            <Grid container spacing={2}>
              <Grid item xs={12}><Field form={draft} path="business.name" label="Nombre del negocio" onChange={change} /></Grid>
              <Grid item xs={12}>
                <LandingCopyTextField
                  field={{ path: "business.shortDescription", label: "Descripción breve" }}
                  form={draft}
                  baselineForm={baselineForm}
                  value={getAtPath(draft, "business.shortDescription", "")}
                  onCommit={(value) => change("business.shortDescription", value)}
                  commitMode="change"
                  fullWidth
                  label="Descripción breve"
                  multiline
                  minRows={3}
                />
              </Grid>
              <Grid item xs={12} sm={6}><Field form={draft} path="business.whatsapp" label="WhatsApp" onChange={change} /></Grid>
              <Grid item xs={12} sm={6}><Field form={draft} path="business.phone" label="Teléfono" onChange={change} /></Grid>
              <Grid item xs={12}><Field form={draft} path="business.email" label="Correo electrónico" type="email" onChange={change} /></Grid>
              <Grid item xs={12}><Field form={draft} path="business.address" label="Dirección" onChange={change} /></Grid>
              <Grid item xs={12} sm={4}><Field form={draft} path="business.city" label="Ciudad" onChange={change} /></Grid>
              <Grid item xs={12} sm={4}><Field form={draft} path="business.province" label="Provincia" onChange={change} /></Grid>
              <Grid item xs={12} sm={4}><Field form={draft} path="business.country" label="País" onChange={change} /></Grid>
              <Grid item xs={12}><Field form={draft} path="business.schedule" label="Horarios" onChange={change} multiline minRows={2} /></Grid>
              <Grid item xs={12}><Field form={draft} path="business.mapEmbedUrl" label="URL de mapa embebido" onChange={change} /></Grid>
              <Grid item xs={12} sm={6}><Field form={draft} path="business.socials.instagram" label="Instagram" onChange={change} /></Grid>
              <Grid item xs={12} sm={6}><Field form={draft} path="business.socials.facebook" label="Facebook" onChange={change} /></Grid>
              <Grid item xs={12} sm={6}><Field form={draft} path="business.socials.linkedin" label="LinkedIn" onChange={change} /></Grid>
              <Grid item xs={12} sm={6}><Field form={draft} path="business.socials.website" label="Sitio externo" onChange={change} /></Grid>
            </Grid>
          ) : null}

          {tab === 4 ? (
            <Stack spacing={2}>
              {mode !== "legacy" ? (
                <>
                  <Box>
                    <Typography sx={{ fontWeight: 950, color: "#19242F" }}>Identidad técnica y compartir</Typography>
                    <Typography variant="body2" sx={{ mt: 0.35, color: "rgba(25,36,47,.62)" }}>
                      Archivos globales que no pertenecen a una sección visual concreta.
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <EditableImage
                        value={getAtPath(draft, "theme.faviconUrl", "")}
                        label="Favicon"
                        helperText="Ícono cuadrado de la pestaña del navegador."
                        disabled={saving}
                        accept="image/svg+xml,image/png,image/webp,image/x-icon,.svg,.ico"
                        onUpload={(file, options) => upload("theme.faviconUrl", file, { sectionKey: "branding", assetKey: "favicon" }, options)}
                        onRemove={() => remove("theme.faviconUrl")}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <EditableImage
                        value={getAtPath(draft, "seo.imageUrl", "")}
                        label="Imagen para compartir"
                        helperText="Vista previa global para redes sociales y mensajería."
                        disabled={saving}
                        onUpload={(file, options) => upload("seo.imageUrl", file, { sectionKey: "seo", assetKey: "social" }, options)}
                        onRemove={() => remove("seo.imageUrl")}
                      />
                    </Grid>
                  </Grid>
                  <Divider />
                </>
              ) : null}
              <FormControl fullWidth disabled={busy}>
                <InputLabel>Estado público</InputLabel>
                <Select
                  label="Estado público"
                  value={selectedPublicStatus}
                  onChange={(event) => {
                    change("publicStatus", event.target.value);
                    setPendingPublicStatus("");
                  }}
                  inputProps={{ "aria-describedby": "public-status-help" }}
                >
                  <MenuItem value="draft">Borrador</MenuItem>
                  <MenuItem value="published">Publicado</MenuItem>
                  <MenuItem value="maintenance">Mantenimiento</MenuItem>
                </Select>
              </FormControl>
              <Typography id="public-status-help" variant="body2" sx={{ color: "text.secondary" }}>
                Estado actual del servidor: <strong>{publicStatusLabel(currentPublicStatus)}</strong>.
                La selección no modifica la web hasta que confirmes la transición.
              </Typography>
              <Field form={draft} path="seo.title" label="Título para Google y redes" onChange={change} />
              <Field form={draft} path="seo.description" label="Descripción para Google y redes" onChange={change} multiline minRows={4} />
              <Field form={draft} path="messages.maintenanceTitle" label="Título de mantenimiento" onChange={change} />
              <Field form={draft} path="messages.maintenanceDescription" label="Mensaje de mantenimiento" onChange={change} multiline minRows={3} />
              <Alert severity={selectedPublicStatus === "published" ? "success" : "warning"}>
                {selectedPublicStatus === "published"
                  ? "Publicado toma siempre el último borrador guardado y crea una revisión pública real."
                  : selectedPublicStatus === "maintenance"
                    ? "Mantenimiento conserva la última publicación y muestra solamente el aviso público."
                    : "Borrador oculta la web pública sin borrar publicaciones ni revisiones anteriores."}
              </Alert>
              {pendingPublicStatus ? (
                <Alert severity="info" role="alert">
                  <Typography sx={{ fontWeight: 900 }}>
                    Confirmar cambio a {publicStatusLabel(pendingPublicStatus)}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {statusConfirmationMessage(pendingPublicStatus)}
                  </Typography>
                </Alert>
              ) : null}
            </Stack>
          ) : null}

          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 1.5, sm: 3 },
          py: { xs: 1.25, sm: 2 },
          alignItems: { xs: "stretch", sm: "center" },
          flexDirection: { xs: "column-reverse", sm: "row" },
          gap: 1,
        }}
      >
        {pendingPublicStatus ? (
          <>
            <Button
              onClick={() => setPendingPublicStatus("")}
              disabled={busy}
              sx={{ textTransform: "none", fontWeight: 850, width: { xs: "100%", sm: "auto" } }}
            >
              Volver
            </Button>
            <Button
              variant="contained"
              onClick={() => persist(pendingPublicStatus)}
              disabled={busy}
              startIcon={busy ? <WebProfitSpinner size={16} /> : undefined}
              sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900, bgcolor: "#F35A3A", width: { xs: "100%", sm: "auto" } }}
            >
              {busy ? "Aplicando…" : `Confirmar ${publicStatusLabel(pendingPublicStatus)}`}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={busy} sx={{ textTransform: "none", fontWeight: 850, width: { xs: "100%", sm: "auto" } }}>Cancelar</Button>
            <Button variant="contained" onClick={save} disabled={busy} startIcon={busy ? <WebProfitSpinner size={16} /> : undefined} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900, bgcolor: "#F35A3A", width: { xs: "100%", sm: "auto" } }}>
              {busy ? "Guardando…" : publicStatusChanged ? "Revisar transición" : mode === "sections" ? "Guardar secciones" : mode === "identity" ? "Guardar datos" : "Guardar preferencias"}
            </Button>
          </>
        )}
      </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(pendingCustomRemoval) && open}
        onClose={() => setPendingCustomRemovalId("")}
        fullWidth
        maxWidth="sm"
        aria-labelledby="remove-custom-section-dialog-title"
        aria-describedby="remove-custom-section-dialog-description"
      >
        <DialogTitle
          id="remove-custom-section-dialog-title"
          sx={{ fontWeight: 950, color: "#19242F", overflowWrap: "anywhere" }}
        >
          ¿Quitar “{pendingCustomRemovalLabel}”?
        </DialogTitle>
        <DialogContent>
          <Typography
            id="remove-custom-section-dialog-description"
            variant="body2"
            sx={{ color: "rgba(25,36,47,.68)", overflowWrap: "anywhere" }}
          >
            Esta sección libre y el contenido creado dentro de ella se quitarán de estos ajustes.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            pb: { xs: 2, sm: 2.5 },
            flexDirection: { xs: "column-reverse", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: 1,
          }}
        >
          <Button
            onClick={() => setPendingCustomRemovalId("")}
            sx={{ textTransform: "none", fontWeight: 850, width: { xs: "100%", sm: "auto" } }}
          >
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmCustomRemoval}
            disabled={busy}
            sx={{ textTransform: "none", fontWeight: 900, width: { xs: "100%", sm: "auto" } }}
          >
            Quitar sección
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
